import axiosInstance from "../axios/axiosInstance";

export interface UserPermissions {
  permissions: string[];
  isSuperAdmin: boolean;
  roleName?: string;
  roleSlug?: string;
}

/**
 * Get current user's permissions from backend
 * Fetches from /api/auth/me endpoint which returns user with roleEntity and permissions
 */
export const getUserPermissions = async (): Promise<UserPermissions> => {
  try {
    const role = Number(localStorage.getItem("role"));
    const token = localStorage.getItem("token");

    if (!token) {
      return {
        permissions: [],
        isSuperAdmin: false,
      };
    }

    // Super Admin exception - check localStorage first for quick access
    const isSuperAdminFromStorage = localStorage.getItem("isSuperAdmin") === "true";
    const permissionsStr = localStorage.getItem("permissions");
    let permissions: string[] = [];
    try {
      permissions = permissionsStr ? JSON.parse(permissionsStr) : [];
    } catch (e) {
      // Ignore parse errors
    }
    
    // Users with "admin" permission have all permissions like Super Admin (but are NOT Super Admin)
    const hasAllPermissions = permissions.includes("admin");
    // isSuperAdmin should only be true for actual Super Admin (role = 0), not for admin permission users
    const isSuperAdmin = role === 0 || isSuperAdminFromStorage;
    
    if (isSuperAdmin) {
      return {
        permissions: ["*"], // Wildcard means all permissions
        isSuperAdmin: true,
        roleName: "Super Admin",
        roleSlug: "super_admin",
      };
    }

    // Try to get from localStorage first (cached from login)
    const roleEntityStr = localStorage.getItem("roleEntity");
    
    if (permissionsStr && roleEntityStr) {
      try {
        const roleEntity = JSON.parse(roleEntityStr);
        
        return {
          permissions: permissions || [],
          isSuperAdmin: false,
          roleName: roleEntity?.name,
          roleSlug: roleEntity?.slug,
        };
      } catch (e) {
        // If parsing fails, fetch from backend
      }
    }

    // Fetch from backend API
    const resp = await axiosInstance.get<{ status: string; data: any }>("/api/auth/me");
    
    if (resp.status === 200 && resp.data?.status === "ok") {
      const userData = resp.data.data;
      const permissions = userData?.roleEntity?.permissions || [];
      const isSuperAdminRole = userData?.role === 0 || userData?.roleEntity?.slug === "super_admin";
      // Users with "admin" permission have all permissions like Super Admin (but are NOT Super Admin)
      const hasAllPermissions = permissions.includes("admin");
      // isSuperAdmin should only be true for actual Super Admin (role = 0), not for admin permission users
      const isSuperAdmin = isSuperAdminRole;
      
      // Update localStorage cache
      if (permissions.length > 0) {
        localStorage.setItem("permissions", JSON.stringify(permissions));
      }
      if (userData?.roleEntity) {
        localStorage.setItem("roleEntity", JSON.stringify(userData.roleEntity));
        localStorage.setItem("isSuperAdmin", isSuperAdmin ? "true" : "false");
      }
      
      return {
        permissions: isSuperAdmin ? ["*"] : permissions,
        isSuperAdmin: isSuperAdmin,
        roleName: userData?.roleEntity?.name,
        roleSlug: userData?.roleEntity?.slug,
      };
    }
    
    return {
      permissions: [],
      isSuperAdmin: false,
    };
  } catch (error: any) {
    console.error("Error getting user permissions:", error);
    
    // Fallback to localStorage if API fails
    const permissionsStr = localStorage.getItem("permissions");
    const fallbackRole = Number(localStorage.getItem("role"));
    const isSuperAdminRole = localStorage.getItem("isSuperAdmin") === "true" || fallbackRole === 0;
    
    if (permissionsStr) {
      try {
        const permissions = JSON.parse(permissionsStr);
        // Users with "admin" permission have all permissions like Super Admin (but are NOT Super Admin)
        const hasAllPermissions = permissions.includes("admin");
        // isSuperAdmin should only be true for actual Super Admin (role = 0), not for admin permission users
        const isSuperAdmin = isSuperAdminRole;
        
        return {
          permissions: isSuperAdmin ? ["*"] : permissions,
          isSuperAdmin: isSuperAdmin,
        };
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    return {
      permissions: [],
      isSuperAdmin: false,
    };
  }
};

/**
 * Check if user has a specific permission
 * This is a synchronous version that uses cached permissions
 */
export const hasPermissionSync = (permission: string | string[]): boolean => {
  const role = Number(localStorage.getItem("role"));
  const isSuperAdminRole = role === 0;
  const permissionsStr = localStorage.getItem("permissions");
  
  if (!permissionsStr) {
    return isSuperAdminRole;
  }
  
  try {
    const permissions: string[] = JSON.parse(permissionsStr);
    
    // Super Admin (role = 0) has all permissions
    if (isSuperAdminRole) {
      return true;
    }
    
    // Users with "admin" permission have all permissions (but are NOT Super Admin)
    if (permissions.includes("admin")) {
      return true;
    }
    
    // Helper function to check if a permission matches (handles member/user equivalence)
    const checkPermission = (requiredPerm: string): boolean => {
      // Direct permission match
      if (permissions.includes(requiredPerm)) return true;
      
      // Check for .manage permission
      const basePerm = requiredPerm.split('.')[0];
      if (permissions.includes(`${basePerm}.manage`)) {
        return true;
      }
      
      // Handle member/user equivalence (member.* and user.* refer to the same resource)
      if (basePerm === "user" || basePerm === "member") {
        const alternateBase = basePerm === "user" ? "member" : "user";
        const alternatePerm = requiredPerm.replace(basePerm, alternateBase);
        if (permissions.includes(alternatePerm)) return true;
        if (permissions.includes(`${alternateBase}.manage`)) return true;
      }
      
      return false;
    };
    
    // If permission is an array, check if user has at least one
    if (Array.isArray(permission)) {
      return permission.some(perm => checkPermission(perm));
    }
    
    // Single permission check
    return checkPermission(permission);
    
    return false;
  } catch (e) {
    return false;
  }
};

/**
 * Check if user has a specific permission (async version)
 */
export const hasPermission = async (permission: string | string[]): Promise<boolean> => {
  const userPerms = await getUserPermissions();
  
  // Super Admin (role = 0) or users with "admin" permission have all permissions
  if (userPerms.isSuperAdmin) {
    return true;
  }
  
  // Check for "admin" permission (users with admin permission have all permissions, but are NOT Super Admin)
  if (userPerms.permissions.includes("admin")) {
    return true;
  }
  
    // Helper function to check if a permission matches (handles member/user equivalence)
    const checkPermission = (requiredPerm: string): boolean => {
      // Direct permission match
      if (userPerms.permissions.includes(requiredPerm)) return true;
      
      // Check for .manage permission
      const basePerm = requiredPerm.split('.')[0];
      if (userPerms.permissions.includes(`${basePerm}.manage`)) {
        return true;
      }
      
      // Handle member/user equivalence (member.* and user.* refer to the same resource)
      if (basePerm === "user" || basePerm === "member") {
        const alternateBase = basePerm === "user" ? "member" : "user";
        const alternatePerm = requiredPerm.replace(basePerm, alternateBase);
        if (userPerms.permissions.includes(alternatePerm)) return true;
        if (userPerms.permissions.includes(`${alternateBase}.manage`)) return true;
      }
      
      return false;
    };
    
    // If permission is an array, check if user has at least one
    if (Array.isArray(permission)) {
      return permission.some(perm => checkPermission(perm));
    }
    
    // Single permission check
    return checkPermission(permission);
  
  return false;
};
